import { Box, Stack, Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';

import {
  DocumentCard,
  DocumentFilters,
  FileDropzone,
  IDocumentFile,
  IDocumentFilters,
  useDocumentFilters,
} from 'core/components';
import { Loader } from 'core/elements';
import { FileExtension, FileUploadType, MimeType, NotificationPosition } from 'core/enums';
import { useFilesActions } from 'core/hooks';
import { useGetPricingProfileDocuments, usePricingProfileDocumentActions } from 'pricing/hooks';
import { IPricingProfile } from 'pricing/types';
import { Color, Direction, Level } from 'theme/enums';

interface IProps {
  pricingProfile: IPricingProfile;
  isReadOnly?: boolean;
}

export const DocumentList = ({ pricingProfile, isReadOnly = false }: IProps) => {
  const { t } = useTranslation();

  const { removePricingProfileDocument, savePricingProfileDocument } =
    usePricingProfileDocumentActions();
  const { fileUpload } = useFilesActions({ position: NotificationPosition.TOP });

  const { records, isLoading } = useGetPricingProfileDocuments(pricingProfile.id);

  const {
    filters,
    setFilters,
    getActiveFilterCount,
    clearFilters,
    filteredRecords,
    fileTypeOptions,
  } = useDocumentFilters<IDocumentFilters, IDocumentFile>({
    records: records.map((doc) => ({
      ...doc,
      fileName: doc.name,
    })),
  });

  const onRemoveFile = async (fileId: number) => {
    await removePricingProfileDocument({
      pricingProfileId: pricingProfile.id,
      ids: [fileId],
    });
  };

  const acceptFileTypes = {
    [MimeType.XLS]: [FileExtension.XLS],
    [MimeType.XLSX]: [FileExtension.XLSX],
    [MimeType.CSV]: [FileExtension.CSV],
    [MimeType.PDF]: [FileExtension.PDF],
    [MimeType.JPEG]: [FileExtension.JPEG],
    [MimeType.PNG]: [FileExtension.PNG],
  };

  const onUpload = async (files: File[]) => {
    const { data } = await fileUpload(files[0], {
      fileType: FileUploadType.PRICING_PROFILE_ATTACHMENT,
    });

    if (!data?.[0]?.id) {
      return;
    }

    const fileId = data[0].id;
    const fileName = files[0].name;

    await savePricingProfileDocument({
      name: fileName,
      fileId,
      pricingProfileId: pricingProfile.id,
    });
  };

  return (
    <Stack gap={1}>
      {!isReadOnly && (
        <FileDropzone accept={acceptFileTypes} onUpload={onUpload} style={{ height: '113px' }} />
      )}

      {records.length > 0 && (
        <DocumentFilters
          filters={filters}
          setFilters={setFilters}
          activeFilterCount={getActiveFilterCount()}
          clearFilters={clearFilters}
          fileTypeOptions={fileTypeOptions}
        />
      )}

      <Box>
        {isLoading && <Loader />}
        {!isLoading && filteredRecords.length === 0 && (
          <Typography color={Color.NEUTRAL} level={Level.TITLE_SM} fontStyle="italic">
            {records.length ? t('messageNoMatchingFiles') : t('messageNoFilesAttached')}
          </Typography>
        )}
      </Box>

      <Stack
        direction={Direction.ROW}
        sx={{
          gap: 2,
          flexWrap: 'wrap',
          maxHeight: 'calc(100vh - 480px)',
          overflow: 'auto',
        }}
      >
        {filteredRecords.map((document) => (
          <DocumentCard
            key={document.id}
            file={document}
            onRemove={isReadOnly ? undefined : onRemoveFile}
          />
        ))}
      </Stack>
    </Stack>
  );
};
